import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LIVE_TAP_EVENT_COUNT_THRESHOLD } from '@zelisight/contract';
import { ArrowRightIcon, XIcon } from 'lucide-react';

import { useKeys, useOnboarding } from '@/api/queries';
import { canSeeKeyValue, type WriteKey } from '@/api/schemas';
import { ErrorState } from '@/components/ErrorState';
import {
  findEnvironmentMismatch,
  OnboardingEscapeHatch,
} from '@/components/OnboardingEscapeHatch';
import { SnippetTabs } from '@/components/SnippetTabs';
import { WaitingForFirstEvent } from '@/components/WaitingForFirstEvent';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StateBlock } from '@/components/StateBlock';
import { useWorkspace } from '@/lib/workspace';
import { KeyRoundIcon } from 'lucide-react';

/** Plan Phase 3: the escape hatch appears after ten minutes of silence. */
const ESCAPE_HATCH_MS = 10 * 60 * 1000;

/** Where the waiting clock starts, persisted so a reload does not reset it. */
const WAIT_SINCE_KEY = 'zs.onboarding.waitingSince';

function readWaitingSince(projectId: string): number {
  const key = `${WAIT_SINCE_KEY}.${projectId}`;
  try {
    const stored = window.sessionStorage.getItem(key);
    const parsed = stored === null ? Number.NaN : Number(stored);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    const now = Date.now();
    window.sessionStorage.setItem(key, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

/** The production key, which is what a first install should be pasting. */
function pickProductionKey(keys: readonly WriteKey[]): WriteKey | null {
  const active = keys.filter((key) => key.state === 'active');
  return (
    active.find((key) => key.env === 'production') ??
    active[0] ??
    keys[0] ??
    null
  );
}

/**
 * The full bleed first screen, plan Phase 3.
 *
 * Acceptance A3.1 is the constraint that shapes everything here: signup lands
 * directly on this screen with zero wizard steps and zero empty states. The
 * write key already exists because `GET /v1/me` bootstrapped it, the snippet is
 * already complete, and the only thing left for the customer to do is paste.
 */
export function Onboarding() {
  const workspace = useWorkspace();
  const projectId = workspace.project.project_id;

  const [waitingSince] = useState(() => readWaitingSince(projectId));
  const [dismissed, setDismissed] = useState(false);
  const [escapeHatchDue, setEscapeHatchDue] = useState(
    () => Date.now() - waitingSince >= ESCAPE_HATCH_MS,
  );

  const keys = useKeys(projectId);
  const onboarding = useOnboarding(projectId, !dismissed);

  const received = onboarding.data?.first_event_at != null;
  const eventCount = onboarding.data?.event_count ?? 0;

  // Acceptance A3.10: past a hundred events the panel has made its point and
  // collapses to a dismissible banner.
  const established = eventCount >= LIVE_TAP_EVENT_COUNT_THRESHOLD;

  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (received || escapeHatchDue) return;
    const remaining = ESCAPE_HATCH_MS - (Date.now() - waitingSince);
    if (remaining <= 0) {
      setEscapeHatchDue(true);
      return;
    }
    timerRef.current = window.setTimeout(() => setEscapeHatchDue(true), remaining);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [received, escapeHatchDue, waitingSince]);

  const productionKey = useMemo(
    () => (keys.data ? pickProductionKey(keys.data.keys) : null),
    [keys.data],
  );
  const mismatch = useMemo(
    () => (keys.data ? findEnvironmentMismatch(keys.data.keys) : null),
    [keys.data],
  );

  if (established && dismissed) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        This project is receiving events. The install snippet stays available under{' '}
        <Link
          className="text-primary underline-offset-4 hover:underline"
          to={`/p/${encodeURIComponent(projectId)}/settings/keys`}
        >
          Write keys
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="zs-page-header zs-dark-surface space-y-3">
        <p className="zs-eyebrow text-primary">01 / Connect your product</p>
        <h1 className="font-semibold tracking-tight">
          {received ? 'You are receiving events' : 'Install Zeligate'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {received
            ? 'The snippet is working. Keep it for your other environments, or move on to the dashboard.'
            : 'Paste one of these into the app you want to measure. Your production key is already in the code, so there is nothing to fill in.'}
        </p>
      </header>

      <section aria-label="First event status">
        {onboarding.isError ? (
          <ErrorState error={onboarding.error} onRetry={() => void onboarding.refetch()} />
        ) : onboarding.isPending ? (
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-start gap-3">
              <Skeleton className="size-8 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-full max-w-md" />
              </div>
            </div>
          </div>
        ) : (
          <WaitingForFirstEvent onboarding={onboarding.data} waitingSince={waitingSince} />
        )}
      </section>

      {received ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild>
            <Link to={`/p/${encodeURIComponent(projectId)}`}>
              Go to the dashboard
              <ArrowRightIcon className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/p/${encodeURIComponent(projectId)}/live`}>Watch the live feed</Link>
          </Button>
          {established ? (
            <Button variant="ghost" onClick={() => setDismissed(true)}>
              <XIcon className="size-4" aria-hidden />
              Dismiss this screen
            </Button>
          ) : null}
        </div>
      ) : null}

      <section aria-label="Install snippet" className="rounded-[40px] border border-border bg-card p-6 sm:p-8 space-y-5">
        <h2 className="text-sm font-medium">Your snippet</h2>
        {keys.isError ? (
          <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />
        ) : keys.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-44 w-full" />
          </div>
        ) : productionKey === null ? (
          <StateBlock
            icon={KeyRoundIcon}
            tone="warning"
            title="This project has no write key"
            detail="A project is created with one key per environment. Create one to get an install snippet."
            action={
              <Button asChild size="sm">
                <Link to={`/p/${encodeURIComponent(projectId)}/settings/keys`}>
                  Manage write keys
                </Link>
              </Button>
            }
          />
        ) : productionKey.key_value === undefined ? (
          <StateBlock
            icon={KeyRoundIcon}
            tone="warning"
            title="Your role cannot see key values"
            detail={
              canSeeKeyValue(workspace.role)
                ? 'The API did not return the key value for this key.'
                : 'A write key is a write capability, so it is shown to owners and admins only. Ask one of them for the snippet.'
            }
          />
        ) : (
          <SnippetTabs writeKey={productionKey.key_value} />
        )}
      </section>

      {!received && escapeHatchDue ? (
        <section aria-label="Troubleshooting">
          <OnboardingEscapeHatch mismatch={mismatch} />
        </section>
      ) : null}
    </div>
  );
}
